import {
  CaseNumber,
  CaseNumberError,
  isLoaded as isLoadedCN,
  UserAssignedCaseNumber,
} from 'ApplicationEntry/models/CaseNumber';
import {
  isLoaded,
  isReserved,
  PolicyNumber,
  PolicyNumberError,
  PolicyNumberValidating,
  Reserved,
  UserAssignedPolicyNumber,
} from 'ApplicationEntry/models/PolicyNumber';
import {
  EmptyPolicyTypes,
  isLoadedPolicyTypes,
  LoadedPolicyTypes,
  PolicyTypes,
} from 'ApplicationEntry/models/PolicyTypes';
import {Async, Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {Product} from './ProductsRiders/PlanModel';

export class InvalidGroupCaseDetails {
  public readonly key = 'InvalidGroupCaseDetails';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly policyNumber: PolicyNumber,
    public readonly productType: PolicyTypes,
    public readonly caseName: StringUnion,
    public readonly achIndicator: boolean,
    public readonly billingCode: StringUnion,
    public readonly situsState: StringUnion,
    public readonly userNameAuthJStamp: StringUnion,
    public readonly userAuthorizedJStamp: boolean,
    public readonly disableNexttoShowOtherSection: boolean,
    public readonly disableNexttoShowOtherSectionArr: policyNumberDetail[],
    public readonly messages: [],
    public readonly notACH: boolean,
    public readonly policyStatus: StringUnion,
    public readonly managedStatus: StringUnion,
    public readonly managedStatusFlag: boolean,
  ) {}
}

export type policyNumberDetail = {
  policyNumberExists: boolean;
  policyNo: string;
};


type CaseDetailsProperties = {
  caseNumber: CaseNumber;
  policyNumber: PolicyNumber;
  productType: PolicyTypes;
  caseName: StringUnion;  
  achIndicator: boolean;
  billingCode: StringUnion;
  situsState: StringUnion;
  userNameAuthJStamp: StringUnion;
  userAuthorizedJStamp: boolean;
  disableNexttoShowOtherSection: boolean;
  disableNexttoShowOtherSectionArr: policyNumberDetail[];
  messages: [];
  notACH: boolean;
  policyStatus: StringUnion;
  managedStatus: StringUnion;
  managedStatusFlag: boolean;
};

export class ValidGroupCaseDetails {
  public static createFromApp({
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag,
  }: CaseDetailsProperties) {
    return this.create(
      caseNumber,
      policyNumber,
      productType,
      caseName,
      achIndicator,
      billingCode,
      situsState,
      userNameAuthJStamp,
      userAuthorizedJStamp,
      disableNexttoShowOtherSection,
      disableNexttoShowOtherSectionArr,
      messages,
      notACH,
      policyStatus,
      managedStatus,
      managedStatusFlag
    );
  }

  public static create(
    caseNumber: CaseNumber,
    policyNumber: PolicyNumber,
    productType: PolicyTypes,
    caseName: StringUnion,
    achIndicator: boolean,
    billingCode: StringUnion,
    situsState: StringUnion,
    userNameAuthJStamp: StringUnion,
    userAuthorizedJStamp: boolean,
    disableNexttoShowOtherSection: boolean,
    disableNexttoShowOtherSectionArr: policyNumberDetail[],
    messages: [],
    notACH: boolean,
    policyStatus: StringUnion,
    managedStatus: StringUnion,
    managedStatusFlag: boolean,
  ) {
    return isLoadedCN(caseNumber) &&
      isLoadedPolicyTypes(productType) &&
      productType.selectedPolicyTypes.length > 0 &&
      productType.selectedPolicyTypes.length <= 3 &&
      (isLoaded(policyNumber) || isReserved(policyNumber)) &&
      isNonEmptyString(caseName)
      ? new ValidGroupCaseDetails(
          caseNumber,
          policyNumber,
          productType,
          caseName,
          achIndicator,
          billingCode!,
          situsState,
          userNameAuthJStamp,
          userAuthorizedJStamp,
          disableNexttoShowOtherSection,
          disableNexttoShowOtherSectionArr,
          messages,
          notACH,
          policyStatus,
          managedStatus,
          managedStatusFlag,
        )
      : new InvalidGroupCaseDetails(
          caseNumber,
          policyNumber,
          productType,
          caseName,
          achIndicator,
          billingCode!,
          situsState,
          userNameAuthJStamp,
          userAuthorizedJStamp,
          disableNexttoShowOtherSection,
          disableNexttoShowOtherSectionArr,
          messages,
          notACH,
          policyStatus,
          managedStatus,
          managedStatusFlag
        );
  }

  public readonly key = 'ValidGroupCaseDetails';
  private constructor(
    public readonly caseNumber: Loaded<string>,
    public readonly policyNumber: Loaded<string> | Reserved,
    public readonly productType: LoadedPolicyTypes,
    public readonly caseName: StringUnion,
    public readonly achIndicator: boolean,
    public readonly billingCode: StringUnion,
    public readonly situsState: StringUnion,
    public readonly userNameAuthJStamp: StringUnion,
    public readonly userAuthorizedJStamp: boolean,
    public readonly disableNexttoShowOtherSection: boolean,
    public readonly disableNexttoShowOtherSectionArr: policyNumberDetail[],
    public readonly messages: [],
    public readonly notACH: boolean,
    public readonly policyStatus: StringUnion,
    public readonly managedStatus: StringUnion,
    public readonly managedStatusFlag: boolean,
  ) {}
}

export type GroupCaseDetails = ValidGroupCaseDetails | InvalidGroupCaseDetails;

export type GroupCaseDetailsOptions<TReturn> = ToPartialMatchOptions<GroupCaseDetails, 'key', TReturn>;

export const matchGroupCaseDetails = <TReturn>(
  caseDetails: GroupCaseDetails,
  options: GroupCaseDetailsOptions<TReturn>
): TReturn => {
  switch (caseDetails.key) {
    case 'InvalidGroupCaseDetails':
      return options.InvalidGroupCaseDetails
        ? options.InvalidGroupCaseDetails(caseDetails)
        : options.default(caseDetails);
    case 'ValidGroupCaseDetails':
      return options.ValidGroupCaseDetails ? options.ValidGroupCaseDetails(caseDetails) : options.default(caseDetails);
  }
};

export const changeCaseNumber = (caseNumber: string) => ({
  policyNumber,
  productType,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    new UserAssignedCaseNumber(caseNumber),
    policyNumber,
    productType,
    new EmptyString(),
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changePolicyNumber = (policyNumber: PolicyNumber) => ({
  caseNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode!,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr?.length > 0 &&
      disableNexttoShowOtherSectionArr.some(policyNumber => policyNumber.policyNumberExists === true)
      ? (disableNexttoShowOtherSectionArr = [])
      : [],
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const changeUserPolicyNumber = (policyNumber: string) => ({
  caseNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    caseNumber,
    UserAssignedPolicyNumber.create(policyNumber),
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changeReservedPolicyNumber = (policyNumber: string) => ({
  caseNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    caseNumber,
    Reserved.create(policyNumber),
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changeProductType = (productType: PolicyTypes) => ({
  caseNumber,
  policyNumber,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changePolicyNumberError = (policyNumber: string) => ({
  caseNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    caseNumber,
    PolicyNumberError.create(policyNumber),
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changePolicyNumberValidation = (policyNumber: string) => ({
  caseNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) =>
  ValidGroupCaseDetails.create(
    caseNumber,
    PolicyNumberValidating.create(policyNumber),
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
export const changeAsyncCaseNumber = (caseNumber: Async<string>) => ({
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const changeAsyncCaseNumber1 = (caseNumber: Async<string>) => ({
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const changeAsyncCaseNumber2 = (caseNumber: Async<string>) => ({
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const changeCaseNumberError = (caseNumber: string) => (app: GroupCaseDetails) =>
  ValidGroupCaseDetails.createFromApp({...app, caseNumber: CaseNumberError.create(caseNumber)});

export const changeAchErrorCode = (notACH: boolean) => (app: GroupCaseDetails) =>
ValidGroupCaseDetails.createFromApp({...app, notACH});

export const changeCaseName = (caseName: string) => (app: GroupCaseDetails) =>
  ValidGroupCaseDetails.createFromApp({...app, caseName: NonEmptyString.create(caseName)});

export const changeManagedStatus = (managedStatus: string) => (app: GroupCaseDetails) =>
  ValidGroupCaseDetails.createFromApp({...app, managedStatus: NonEmptyString.create(managedStatus)});

export const EmptyGroupCaseDetails = new InvalidGroupCaseDetails(
  new UserAssignedCaseNumber(''),
  new UserAssignedPolicyNumber(''),
  EmptyPolicyTypes,
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  [],
  [],
  false,
  new EmptyString(),
  new EmptyString(),
  false
);

export const changeAsyncBillingModeCaseNumber = (billingCode: string) => ({
  caseNumber,
  policyNumber,
  productType,
  caseName,
  achIndicator,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    NonEmptyString.create(billingCode),
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const changeAsyncSitusStateCaseNumber = (situsState: string) => ({
  caseNumber,
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    NonEmptyString.create(situsState),
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const checkUserNameJStamp = (userName: string) => ({
  caseNumber,
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userAuthorizedJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    NonEmptyString.create(userName),
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

const generatedisableNexttoShowOtherSectionArr = (
  disableNexttoShowOtherSection: boolean,
  enteredPolicyNumber: string,
  disableNexttoShowOtherSectionArr: policyNumberDetail[]
) => {
  if (disableNexttoShowOtherSectionArr?.length === 0) {
    return [
      ...disableNexttoShowOtherSectionArr,
      {policyNumberExists: disableNexttoShowOtherSection, policyNo: enteredPolicyNumber},
    ];
  } else if (
    disableNexttoShowOtherSectionArr?.length > 0 &&
    disableNexttoShowOtherSectionArr.some(policyNumber => policyNumber.policyNo === enteredPolicyNumber)
  ) {
    return disableNexttoShowOtherSectionArr.filter(policyNumber => policyNumber.policyNo === enteredPolicyNumber);
  } else if (
    disableNexttoShowOtherSectionArr?.length > 0 &&
    disableNexttoShowOtherSectionArr?.some(
      policyNumber =>
        policyNumber.policyNo.substring(0, 2) === enteredPolicyNumber.substring(0, 2) &&
        policyNumber.policyNo.charAt(policyNumber.policyNo.length - 1) ===
          enteredPolicyNumber.charAt(enteredPolicyNumber.length - 1)
    )
  ) {
    let matchedPolciyArr = disableNexttoShowOtherSectionArr.filter(
      policyNumber =>
        policyNumber.policyNo.substring(0, 2) === enteredPolicyNumber.substring(0, 2) &&
        policyNumber.policyNo.charAt(policyNumber.policyNo.length - 1) ===
          enteredPolicyNumber.charAt(enteredPolicyNumber.length - 1)
    );
    matchedPolciyArr?.map((item, idx) => {
      item.policyNo = enteredPolicyNumber;
      item.policyNumberExists = disableNexttoShowOtherSection;
    });

    return matchedPolciyArr;
  } else {
    return [
      ...disableNexttoShowOtherSectionArr,
      {policyNumberExists: disableNexttoShowOtherSection, policyNo: enteredPolicyNumber},
    ];
  }
};

export const getACHIndicatorValue = (achIndicator: boolean) => (app: GroupCaseDetails) =>
ValidGroupCaseDetails.createFromApp({...app, achIndicator});

export const updateManagedStatusFlag = (managedStatusFlag: boolean) => (app: GroupCaseDetails) =>
ValidGroupCaseDetails.createFromApp({...app, managedStatusFlag});
  
export const getUserAuthJStamp = (userAuthorizedJStamp: boolean) => ({
  caseNumber,
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  disableNexttoShowOtherSection,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    disableNexttoShowOtherSectionArr,
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};

export const getPolicyNumberExists = (disableNexttoShowOtherSection: boolean, enteredPolicyNumber: string) => ({
  caseNumber,
  policyNumber,
  productType,
  caseName,
  achIndicator,
  billingCode,
  situsState,
  userNameAuthJStamp,
  userAuthorizedJStamp,
  disableNexttoShowOtherSectionArr,
  messages,
  notACH,
  policyStatus,
  managedStatus,
  managedStatusFlag,
}: GroupCaseDetails) => {
  return ValidGroupCaseDetails.create(
    caseNumber,
    policyNumber,
    productType,
    caseName,
    achIndicator,
    billingCode,
    situsState,
    userNameAuthJStamp,
    userAuthorizedJStamp,
    disableNexttoShowOtherSection,
    generatedisableNexttoShowOtherSectionArr(
      disableNexttoShowOtherSection,
      enteredPolicyNumber,
      disableNexttoShowOtherSectionArr
    ),
    messages,
    notACH,
    policyStatus,
    managedStatus,
    managedStatusFlag
  );
};
